"use client";

/**
 * Sonner互換レイヤー for shadcn/ui toast
 *
 * このファイルは、既存の shadcn/ui toast API を維持しながら、
 * 内部的に Sonner を使用するための互換レイヤーです。
 *
 * なぜこの互換レイヤーが必要か：
 * 1. 既存の59ファイルが useToast を使用している
 * 2. shadcn/ui の toast は再レンダリングを引き起こし、ホバー状態が失われる問題がある
 * 3. Sonner は再レンダリングを引き起こさず、より良いパフォーマンスを提供する
 * 4. 全ファイルを書き換えるのは時間がかかり、リスクが高い
 *
 * 互換性の仕組み：
 * - toast() 関数: shadcn/ui の API を受け取り、Sonner の API に変換
 * - useToast() hook: 互換性のために維持（実際の状態管理は Sonner が行う）
 * - variant の変換: shadcn/ui の variant を Sonner の toast タイプに変換
 */

import * as React from "react";
import { toast as sonnerToast } from "sonner";
import type { ToastActionElement, ToastProps } from "@/components/ui/toast";

// 型定義は既存のものを維持して互換性を保つ
type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

type Toast = Omit<ToasterToast, "id">;

// ID生成器（既存の実装を維持）
let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

/**
 * shadcn/ui の variant を Sonner の toast タイプに変換する
 *
 * shadcn/ui variants:
 * - "default" (または未指定): 通常の通知
 * - "destructive": エラー通知
 *
 * Sonner types:
 * - toast() または toast.message(): 通常の通知
 * - toast.success(): 成功通知
 * - toast.error(): エラー通知
 * - toast.warning(): 警告通知
 * - toast.info(): 情報通知
 */
function convertVariantToSonnerType(variant?: ToastProps["variant"]) {
  switch (variant) {
    case "destructive":
      return "error";
    default:
      return "default";
  }
}

/**
 * shadcn/ui toast API を Sonner API に変換して実行する
 *
 * @deprecated この関数は互換性のために残されています。新しいコードでは Sonner を直接使用してください。
 * @param props - shadcn/ui の toast プロパティ
 * @returns toast の ID と操作メソッド（互換性のため）
 */
function toast({ title, description, variant, action }: Toast): {
  id: string;
  dismiss: () => void;
  update: (props: Partial<ToasterToast>) => ReturnType<typeof toast>;
} {
  const id = genId();
  const type = convertVariantToSonnerType(variant);

  // タイトルとdescriptionを組み合わせてメッセージを作成
  const message = title || "";
  const descriptionText = description ? String(description) : undefined;

  // UI/UX改善: titleとdescriptionが同じ内容の場合、descriptionを省略する
  // 理由: 同じ内容が2回表示されると冗長で、ユーザーに混乱を与える可能性がある
  // 例: "コピーしました" というメッセージが2回表示されるのを防ぐ
  const shouldOmitDescription =
    title && description && String(title).trim() === String(description).trim();

  // Sonner のオプション
  const sonnerOptions = {
    id,
    description: shouldOmitDescription ? undefined : descriptionText,
    // action は Sonner でサポートされていないため、説明文に含める
    // 実際のアクションボタンが必要な場合は、Sonner のカスタムレンダリングを使用する必要がある
    ...(action && {
      description: shouldOmitDescription
        ? undefined
        : descriptionText || "Action required",
    }),
  };

  // variant に応じて適切な Sonner メソッドを呼び出す
  let toastId: string | number;
  if (type === "error") {
    toastId = sonnerToast.error(message, sonnerOptions);
  } else {
    // 通常のトーストも成功トーストとして表示（黒っぽい背景を適用するため）
    toastId = sonnerToast.success(message, sonnerOptions);
  }

  // 互換性のために既存の API 形式を返す
  return {
    id: String(toastId),
    // Sonner の dismiss メソッドをラップ
    dismiss: () => sonnerToast.dismiss(toastId),
    // update は Sonner でサポートされていないため、新しい toast を表示
    update: (props: Partial<ToasterToast>): ReturnType<typeof toast> => {
      sonnerToast.dismiss(toastId);
      return toast({ ...props });
    },
  };
}

/**
 * useToast hook の互換実装
 *
 * @deprecated このhookは互換性のために残されています。新しいコードでは Sonner を直接使用してください。
 *
 * 注意: Sonner は内部で状態を管理するため、
 * このhookは主に互換性のために存在します。
 * toasts の配列は常に空になります（Sonner が独自に管理するため）。
 */
function useToast() {
  // 互換性のために空の状態を提供
  const [state] = React.useState<{ toasts: ToasterToast[] }>({ toasts: [] });

  return {
    ...state,
    toast,
    // Sonner の dismiss をラップ
    dismiss: (toastId?: string) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        // 全ての toast を dismiss
        sonnerToast.dismiss();
      }
    },
  };
}

// 既存のコードとの互換性のためにエクスポート
export { toast, useToast };
